import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Sun, Moon, Truck } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
  { label: "WHY BREETHE", href: "#manifesto", testid: "nav-link-manifesto" },
  { label: "THE STRIP", href: "#product", testid: "nav-link-product" },
  { label: "PRICING", href: "#pricing", testid: "nav-link-pricing" },
  { label: "STORIES", href: "#stories", testid: "nav-link-stories" },
  { label: "AMBASSADORS", href: "/ambassadors", testid: "nav-link-ambassadors" },
];

const Navbar = ({ theme, onToggleTheme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [freePostage, setFreePostage] = useState(false);
  const { pathname } = useLocation();

  // Hash anchors work in place on the landing page; elsewhere they go home first
  const hrefFor = (h) => (pathname === "/" ? h : `/${h}`);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/shipping/settings`)
      .then(({ data }) => {
        if (alive) setFreePostage(!!data.free_all);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!freePostage) return;
    const prev = document.body.style.paddingTop;
    document.body.style.paddingTop = "36px";
    return () => {
      document.body.style.paddingTop = prev;
    };
  }, [freePostage]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "bg-[#0A0A0A]/85 backdrop-blur-md border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      {freePostage && (
        <div
          data-testid="free-postage-banner"
          className="h-9 bg-volt text-black flex items-center justify-center gap-2 px-4"
        >
          <Truck size={13} strokeWidth={2.5} />
          <span className="font-plex text-[10px] uppercase tracking-[0.25em]">
            Free postage on all orders — Australia-wide
          </span>
        </div>
      )}
      <nav className="flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <a
          href={hrefFor("#top")}
          data-testid="nav-logo"
          className="flex items-start gap-0.5"
        >
          <img src="/breethe-logo.webp" alt="Breethe" className="brand-logo h-6 md:h-8 w-auto" />
          <span className="text-volt font-anton text-xs mt-0.5">®</span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((link) =>
            link.href.startsWith("/") ? (
              <Link
                key={link.href}
                to={link.href}
                data-testid={link.testid}
                className="group relative font-plex text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors duration-300"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-volt transition-[width] duration-300 group-hover:w-full" />
              </Link>
            ) : (
              <a
                key={link.href}
                href={hrefFor(link.href)}
                data-testid={link.testid}
                className="group relative font-plex text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors duration-300"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-volt transition-[width] duration-300 group-hover:w-full" />
              </a>
            )
          )}
          <button
            onClick={onToggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle light theme"
            className="w-11 h-11 border border-white/20 flex items-center justify-center text-white/70 transition-[color,border-color,transform] duration-300 hover:text-volt hover:border-volt active:scale-95"
          >
            {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
          </button>
          <Link
            to="/buy"
            data-testid="nav-cta-button"
            className="bg-volt text-black font-anton uppercase text-sm px-6 py-3 tracking-wider transition-[transform,background-color] duration-200 hover:bg-volt-dark active:scale-95"
          >
            Buy now
          </Link>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <Link
            to="/buy"
            data-testid="nav-cta-mobile"
            className="bg-volt text-black font-anton uppercase text-xs px-4 py-2.5 tracking-wider transition-[transform,background-color] duration-200 hover:bg-volt-dark active:scale-95"
          >
            Buy now
          </Link>
          <button
            data-testid="mobile-menu-button"
            className="text-white p-2 active:scale-95 transition-transform"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0A0A] border-t border-white/10"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((link) =>
                link.href.startsWith("/") ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`mobile-${link.testid}`}
                    onClick={() => setOpen(false)}
                    className="font-anton uppercase text-2xl text-white/80 hover:text-volt transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={hrefFor(link.href)}
                    data-testid={`mobile-${link.testid}`}
                    onClick={() => setOpen(false)}
                    className="font-anton uppercase text-2xl text-white/80 hover:text-volt transition-colors"
                  >
                    {link.label}
                  </a>
                )
              )}
              <Link
                to="/buy"
                data-testid="mobile-nav-cta-button"
                onClick={() => setOpen(false)}
                className="bg-volt text-black font-anton uppercase text-base px-6 py-4 text-center tracking-wider active:scale-95 transition-transform"
              >
                Buy now
              </Link>
              <button
                onClick={onToggleTheme}
                data-testid="mobile-theme-toggle"
                className="border border-white/20 text-white/70 font-anton uppercase text-base px-6 py-4 flex items-center justify-center gap-3 active:scale-95 transition-transform"
              >
                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                {theme === "dark" ? "Light Mode" : "Dark Mode"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
